import {
  WARRANTY_CUSTOM_LINE_ITEM_TYPE,
  DELIVERY_FEE_CUSTOM_LINE_ITEM_TYPE,
  CHARGE_AFTER_CHANNEL_ECOMM,
  CHARGE_AFTER_CHANNEL_IN_STORE,
} from '../../utils/common/constants';

const { calculateTotalGiftCards } = require('../../utils');

const buildShippingInfo = ({ cart }) => ({
  line1: cart.shippingAddress.streetName,
  line2: cart.shippingAddress.additionalStreetInfo,
  city: cart.shippingAddress.city,
  zipCode: cart.shippingAddress.postalCode,
  state: cart.shippingAddress.state,
});

const buildBillingInfo = ({ cart }) => {
  const address = cart.billingAddress || cart.shippingAddress;
  return {
    line1: address.streetName,
    line2: address.additionalStreetInfo,
    city: address.city,
    zipCode: address.postalCode,
    state: address.state,
  };
};

const buildCustomerDetailsInfo = ({ cart }) => {
  const address = cart.billingAddress || cart.shippingAddress;
  return {
    consumerDetails: {
      firstName: address.firstName,
      lastName: address.lastName,
      email: cart.shippingAddress.email,
      mobilePhoneNumber: cart.shippingAddress.phone,
      shippingAddress: buildShippingInfo({ cart }),
      billingAddress: buildBillingInfo({ cart }),
    },
  };
};

const buildDiscounts = ({ cart }) => {
  const { giftCards = [] } = cart;

  if (!giftCards.length) {
    return { discounts: [] };
  }

  return {
    discounts: giftCards.map((giftCard) => ({
      amount:
        giftCard.amount.centAmount /
        10 ** (giftCard.amount?.fractionDigits ?? 2),
      name: giftCard.giftCardNumber,
    })),
  };
};

const buildTotal = ({ cart }) => {
  const totalGiftCards = calculateTotalGiftCards(cart);

  const cartTotal = cart.taxedPrice.totalGross.centAmount;

  const total = cartTotal - totalGiftCards;

  return total <= 0 ? 0 : total;
};

const buildCartDetailsInfo = ({ cart, locale }) => {
  const total = buildTotal({ cart });
  const warrantyCustomLineItems = cart.customLineItems?.filter(
    (customLineItem) =>
      customLineItem.custom?.fields?.type === WARRANTY_CUSTOM_LINE_ITEM_TYPE,
  );

  const recyclingFeeCustomLineItems =
    cart?.customLineItems
      ?.filter(
        (customLineItem) => customLineItem?.slug === 'custom_recyclingFee',
      )
      ?.map((lineItem) => ({
        name: lineItem.name?.[locale],
        price:
          lineItem.money?.centAmount / 10 ** lineItem?.money?.fractionDigits,
        sku: lineItem.id,
        quantity: lineItem.quantity,
        leasable: true,
        productCategory: 'Recycling-Fee',
      })) ?? [];

  const deliveryFeeLineItems =
    cart?.customLineItems
      ?.filter(
        (customLineItem) =>
          customLineItem.custom?.fields?.type ===
          DELIVERY_FEE_CUSTOM_LINE_ITEM_TYPE,
      )
      ?.map((lineItem) => ({
        name: lineItem.name?.[locale],
        price:
          lineItem.money?.centAmount / 10 ** lineItem?.money?.fractionDigits,
        sku: lineItem.id,
        quantity: lineItem.quantity,
        leasable: true,
        productCategory: DELIVERY_FEE_CUSTOM_LINE_ITEM_TYPE,
      })) ?? [];

  const lineItemsItems = cart.lineItems.map((lineItem) => {
    const foundWarranty = warrantyCustomLineItems.find((item) =>
      item.custom?.fields?.lineItemsIds?.includes(lineItem.id),
    );
    return {
      name: lineItem.name?.[locale],
      price:
        lineItem.price?.value.centAmount /
        10 ** lineItem?.price?.value.fractionDigits,
      sku: lineItem.id,
      quantity: lineItem.quantity,
      leasable: true,
      productCategory: lineItem.productType?.obj?.description,
      ...(foundWarranty
        ? {
            warranty: {
              name: foundWarranty.name?.[locale],
              price: foundWarranty.custom?.fields?.warrantyPrice / 100,
              sku: foundWarranty.id,
            },
          }
        : {}),
    };
  });

  const totalAmount = total / 10 ** cart.taxedPrice.totalGross.fractionDigits;

  const taxAmount = cart.taxedPrice?.taxPortions
    ? cart.taxedPrice.taxPortions.reduce(
        (totalTaxAmount, taxPortion) =>
          totalTaxAmount + taxPortion.amount.centAmount,
        0,
      ) /
      10 ** (cart.totalGross?.fractionDigits ?? 2)
    : 0;

  const selectedShippingMethods =
    cart.customLineItems?.filter(
      (customLineItem) =>
        customLineItem?.custom?.fields?.netsuite_shipping_name &&
        customLineItem?.custom?.fields?.lineItemsIds.length > 0,
    ) ?? [];

  const shippingAmount =
    selectedShippingMethods.reduce(
      (acc, cur) => acc + cur.money.centAmount,
      0,
    ) /
    10 ** (selectedShippingMethods?.[0]?.money.fractionDigits ?? 2);

  return {
    cartDetails: {
      ...buildDiscounts({ cart }),
      items: [
        ...lineItemsItems,
        ...recyclingFeeCustomLineItems,
        ...deliveryFeeLineItems,
      ],
      taxAmount,
      shippingAmount,
      totalAmount,
    },
  };
};

const buildChargeAfterObject = ({ cart, locale, onDataUpdate, callback }) => {
  const isDraft = cart?.isDraft ?? false;
  const storeKey = cart?.store?.key ?? null;

  return {
    ...buildCustomerDetailsInfo({ cart, locale }),
    ...buildCartDetailsInfo({ cart, locale }),
    onDataUpdate,
    callback,
    channel:
      isDraft && storeKey
        ? CHARGE_AFTER_CHANNEL_IN_STORE
        : CHARGE_AFTER_CHANNEL_ECOMM,
  };
};

const chargeAfterService = {
  buildChargeAfterObject,
};

export default chargeAfterService;
